import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  Chip,
  TablePagination,
  CircularProgress,
  Avatar,
  ListItemAvatar,
  ListItemText,
  Card,
  CardContent,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  AccessTime as TimeIcon,
  Refresh as RefreshIcon,
  Event as CalendarIcon,
  ViewList as ListViewIcon,
  CalendarMonth as CalendarViewIcon
} from '@mui/icons-material';
import { useCurrentUser } from '../hooks/useCurrentUser';
import salesPersonService from '../services/salesPersonService';
import { timeEntryService } from '../services/timeEntryService';
import { getCalendarEventsForUserAndMonth, getCalendarAvailabilityForUserAndMonth } from '../services/graphService';
import UserDisplay from '../components/UserDisplay';
import { graphUserService } from '../services/graphUserService';
import { logError, formatErrorMessage } from '../utils/errorLogger';

const TimeTracker = () => {
  const currentUser = useCurrentUser();

  // Convert date to Eastern timezone
  const convertToEasternTime = (date) => {
    const inputDate = new Date(date);
    const easternDate = new Date(inputDate.toLocaleString('en-US', {
      timeZone: 'America/New_York'
    }));
    
    // Add timezone offset to compensate for local conversion
    const offset = inputDate.getTimezoneOffset();
    easternDate.setMinutes(easternDate.getMinutes() + offset);
    
    return easternDate;
  };

  // Format date to YYYY-MM-DD in Eastern timezone
  const formatDateToEasternYYYYMMDD = (date) => {
    if (!date) return '';
    const easternDate = convertToEasternTime(date);
    const year = easternDate.getFullYear();
    const month = String(easternDate.getMonth() + 1).padStart(2, '0');
    const day = String(easternDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper functions
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      // Parse the YYYY-MM-DD string into a Date object
      const [year, month, day] = dateString.split('-');
      const date = new Date(year, parseInt(month) - 1, parseInt(day));
      
      // Format the date in Eastern timezone
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: 'America/New_York'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  };

  // State management
  const [timeEntries, setTimeEntries] = useState([]);
  const [salesPersons, setSalesPersons] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [loadingSalesPersons, setLoadingSalesPersons] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [userDetails, setUserDetails] = useState({});

  // View toggle state
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'calendar'

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0], // Simple initial date
    description: '',
    hours: ''
  });

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState(null);

  // Add ref to track loading state
  const loadingRef = useRef(false);
  const userManuallySelectedRef = useRef(false);

  // Update initial form date to Eastern timezone after component mounts
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      date: formatDateToEasternYYYYMMDD(new Date())
    }));
  }, []);

  // Debug log for currentUser changes
  useEffect(() => {
    // Remove console.log for currentUser changes
  }, [currentUser]);

  // Load sales persons with Graph API details
  const loadSalesPersons = useCallback(async () => {
    setLoadingSalesPersons(true);
    try {
      const salesPersonsData = await salesPersonService.getSalesPersons();
      
      // Fetch user details for each salesperson
      const detailsPromises = salesPersonsData.map(async (sp) => {
        // Handle both lowercase and uppercase Email field from SQL Server
        const userEmail = sp.Email || sp.email;
        if (!userEmail) {
          console.warn('No email found for salesperson:', sp);
          return sp;
        }
        
        const details = await graphUserService.getUserDetails(userEmail);
        return {
          ...sp,
          email: userEmail, // Normalize to lowercase for consistency
          displayName: details.displayName,
          avatarUrl: details.avatarUrl
        };
      });

      const salesPersonsWithDetails = await Promise.all(detailsPromises);
      
      // Sort alphabetically by display name
      const sortedSalesPersons = salesPersonsWithDetails.sort((a, b) => {
        const nameA = a.displayName || a.Name || a.name || a.email || '';
        const nameB = b.displayName || b.Name || b.name || b.email || '';
        return nameA.localeCompare(nameB);
      });

      setSalesPersons(sortedSalesPersons);
      
      // Cache user details for quick access
      const detailsMap = {};
      salesPersonsWithDetails.forEach(sp => {
        const userEmail = sp.email || sp.Email;
        if (userEmail) {
          detailsMap[userEmail] = {
            displayName: sp.displayName,
            avatarUrl: sp.avatarUrl
          };
        }
      });
      setUserDetails(detailsMap);
      
      return sortedSalesPersons;
    } catch (err) {
      const errorInfo = logError('TimeTracker', 'loadSalesPersons', err);
      setError('Failed to load users');
      return [];
    } finally {
      setLoadingSalesPersons(false);
    }
  }, []);

  // Initialize sales persons on component mount
  useEffect(() => {
    loadSalesPersons();
  }, []); // loadSalesPersons is stable, only needs to run on mount

  // Auto-select current user when both currentUser and salesPersons are available
  useEffect(() => {
    if (currentUser && salesPersons.length > 0 && !selectedUserId && !userManuallySelectedRef.current) {
      // Try multiple ways to get the current user's email
      const currentUserEmail = currentUser.email || currentUser.mail || currentUser.userPrincipalName;
      
      const currentUserExists = salesPersons.find(sp => 
        (sp.email || sp.Email)?.toLowerCase() === currentUserEmail?.toLowerCase()
      );
      
      if (currentUserExists) {
        setSelectedUserId(currentUserEmail);
      } else if (salesPersons.length > 0) {
        setSelectedUserId(salesPersons[0].email || salesPersons[0].Email);
      }
    }
  }, [currentUser, salesPersons]);

  // Load time entries for selected user and month
  const loadTimeEntries = useCallback(async () => {
    if (!selectedUserId || loadingRef.current) return;

    setLoading(true);
    loadingRef.current = true;
    try {
      // Load manual time entries from SQL Server
      const manualEntries = await timeEntryService.getTimeEntriesByUserAndMonth(
        selectedUserId,
        selectedYear,
        selectedMonth
      );
      
      // Transform manual entries to ensure date is in correct format
      const transformedManualEntries = manualEntries.map(entry => ({
        ...entry,
        id: entry.Id || entry.id,
        date: entry.Date || entry.date,
        description: entry.Description || entry.description,
        hours: entry.Hours || entry.hours,
        userId: entry.UserEmail || entry.userEmail || selectedUserId,
        isCalendarEvent: false
      }));

      // Load calendar events for the selected user
      let calendarEntries = [];
      try {
        calendarEntries = await getCalendarEventsForUserAndMonth(
          selectedUserId,
          selectedYear,
          selectedMonth
        );

        // Transform calendar entries to match manual entry format
        calendarEntries = calendarEntries.map(entry => ({
          ...entry,
          id: entry.id || `calendar-${Math.random().toString(36).substr(2, 9)}`,
          date: entry.date,
          description: entry.description,
          hours: entry.hours,
          isCalendarEvent: true,
          startTime: entry.startTime,
          endTime: entry.endTime,
          userId: selectedUserId
        }));
      } catch (calendarErr) {
        logError('TimeTracker', 'loadCalendarEvents', calendarErr);
        setError('Failed to load calendar events');
        calendarEntries = [];
      }

      // Combine manual and calendar entries, sorted by date
      const allEntries = [...transformedManualEntries, ...calendarEntries].sort((a, b) => {
        // First sort by date
        const dateCompare = a.date.localeCompare(b.date);
        if (dateCompare !== 0) return dateCompare;
        
        // If same date, calendar events come after manual entries
        if (a.isCalendarEvent !== b.isCalendarEvent) {
          return a.isCalendarEvent ? 1 : -1;
        }
        
        // If both are calendar events, sort by start time
        if (a.isCalendarEvent && b.isCalendarEvent) {
          return a.startTime.localeCompare(b.startTime);
        }
        
        // For manual entries on same date, preserve original order
        return 0;
      });
      
      setTimeEntries(allEntries);
    } catch (err) {
      const errorInfo = logError('TimeTracker', 'loadTimeEntries', err, {
        selectedUserId,
        selectedYear,
        selectedMonth
      });
      setError('Failed to load time entries');
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [selectedUserId, selectedYear, selectedMonth]);

  // Load data when dependencies change
  useEffect(() => {
    if (selectedUserId) {
      loadTimeEntries();
    }
    // Reset to first page when month/user changes
    setPage(0);
  }, [selectedUserId, selectedYear, selectedMonth, loadTimeEntries]);

  // Handle user selection change
  const handleUserChange = (event) => {
    userManuallySelectedRef.current = true; // Mark that user manually selected
    setSelectedUserId(event.target.value);
  };

  // Handle month navigation
  const handlePreviousMonth = () => {
    if (selectedMonth === 0) {
      setSelectedMonth(11);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (selectedMonth === 11) {
      setSelectedMonth(0);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Calculate paginated entries
  const paginatedEntries = timeEntries.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Calculate total hours for all entries
  const totalHours = timeEntries.reduce((sum, entry) => sum + (entry.hours || 0), 0);

  // Modal handlers
  const handleAddNew = () => {
    setEditingEntry(null);
    setFormData({
      date: new Date().toISOString().split('T')[0],
      description: '',
      hours: ''
    });
    setModalOpen(true);
  };

  const handleEdit = (entry) => {
    console.log('Editing entry (raw):', entry);
    console.log('Entry date fields:', {
      Date: entry.Date,
      date: entry.date,
      rawDate: entry.Date || entry.date,
      id: entry.Id || entry.id,
      timeEntryId: entry.timeEntryId
    });

    // Ensure we have a valid date to work with
    let dateToUse;
    if (entry.Date || entry.date) {
      dateToUse = formatDateToEasternYYYYMMDD(entry.Date || entry.date);
    } else {
      dateToUse = formatDateToEasternYYYYMMDD(new Date());
    }
    console.log('Formatted date to use:', dateToUse);

    const entryToEdit = {
      ...entry,
      timeEntryId: entry.Id || entry.id || entry.timeEntryId, // Ensure we capture the ID correctly
      date: dateToUse,
      description: entry.Description || entry.description || '',
      hours: entry.Hours || entry.hours || 0
    };
    console.log('Transformed entry for editing:', entryToEdit);

    // Set form data first, then open modal and set editing entry
    setFormData({
      date: dateToUse,
      description: entryToEdit.description,
      hours: entryToEdit.hours.toString()
    });
    
    // Use a small delay to ensure state is updated before opening modal
    setTimeout(() => {
      setEditingEntry(entryToEdit);
      setModalOpen(true);
    }, 0);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditingEntry(null);
    // Reset form data after modal is closed
    setFormData({
      date: formatDateToEasternYYYYMMDD(new Date()),
      description: '',
      hours: ''
    });
  };

  const handleFormChange = (field, value) => {
    console.log(`Form field change - ${field}:`, value);
    setFormData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };
      console.log('Updated form data:', newData);
      return newData;
    });
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError('');

      if (!selectedUserId) {
        throw new Error('No user selected');
      }

      // Only send the required fields for update
      const timeEntryData = {
        date: formData.date,
        description: formData.description,
        hours: parseFloat(formData.hours)
      };

      await timeEntryService.saveTimeEntry(
        timeEntryData,
        selectedUserId,
        editingEntry?.timeEntryId
      );
      
      setSuccess(editingEntry ? 'Time entry updated successfully' : 'Time entry created successfully');
      setModalOpen(false);
      loadTimeEntries(); // Reload the list
    } catch (err) {
      const errorInfo = logError('TimeTracker', 'saveTimeEntry', err, {
        formData,
        selectedUserId,
        editingEntry
      });
      setError(formatErrorMessage(err, 'Failed to save time entry'));
    } finally {
      setLoading(false);
    }
  };

  // Delete handlers
  const handleDeleteClick = (entry) => {
    setEntryToDelete(entry);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!entryToDelete) return;

    try {
      await timeEntryService.deleteTimeEntry(entryToDelete.id);
      setSuccess('Time entry deleted successfully');
      setDeleteDialogOpen(false);
      setEntryToDelete(null);
      loadTimeEntries();
    } catch (err) {
      const errorInfo = logError('TimeTracker', 'deleteTimeEntry', err, {
        entryToDelete
      });
      setError(formatErrorMessage(err, 'Failed to delete time entry'));
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setEntryToDelete(null);
  };

  // View toggle handler
  const handleViewModeChange = (event, newViewMode) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
      // Reset pagination when switching views
      setPage(0);
    }
  };

  // Helper functions
  const formatHours = (hours) => {
    return hours % 1 === 0 ? hours.toString() : hours.toFixed(2);
  };

  const getMonthName = (month) => {
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return monthNames[month];
  };

  // Transform calendar events to time entries
  const transformCalendarEvents = useCallback((events) => {
    return events.map(event => {
      const startDate = new Date(event.start?.dateTime || event.start?.date);
      return {
        id: event.id,
        date: formatDateToEasternYYYYMMDD(startDate),
        description: event.subject || event.summary || 'Calendar Event',
        hours: event.duration || 1, // Default to 1 hour if duration not available
        isCalendarEvent: true,
        userId: selectedUserId
      };
    });
  }, [selectedUserId]);

  // Get first day of month in Eastern timezone
  const getFirstDayOfMonth = (year, month) => {
    const date = new Date(year, month, 1);
    return convertToEasternTime(date).getDay();
  };

  // Get days in month considering Eastern timezone
  const getDaysInMonth = (year, month) => {
    const lastDay = new Date(year, month + 1, 0);
    return convertToEasternTime(lastDay).getDate();
  };

  // Format date for calendar considering Eastern timezone
  const formatDateForCalendar = (year, month, day) => {
    const date = new Date(year, month, day);
    return formatDateToEasternYYYYMMDD(date);
  };

  // Check if a date is today in Eastern timezone
  const isToday = (year, month, day) => {
    const today = convertToEasternTime(new Date());
    const date = convertToEasternTime(new Date(year, month, day));
    return (
      date.getFullYear() === today.getFullYear() &&
      date.getMonth() === today.getMonth() &&
      date.getDate() === today.getDate()
    );
  };

  // Get entries for a specific date
  const getEntriesForDate = (dateString) => {
    return timeEntries.filter(entry => {
      console.log(`Comparing entry date ${entry.date} with ${dateString} for entry:`, entry);
      return entry.date === dateString;
    });
  };

  // Calendar view component
  const CalendarView = () => {
    const daysInMonth = getDaysInMonth(selectedYear, selectedMonth);
    const firstDay = getFirstDayOfMonth(selectedYear, selectedMonth);
    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }

    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    return (
      <Paper sx={{ p: 2 }}>
        {/* Calendar Header */}
        <Box sx={{ display: 'flex', mb: 2 }}>
          {dayNames.map(dayName => (
            <Box key={dayName} sx={{ flex: 1, textAlign: 'center' }}>
              <Typography 
                variant="subtitle2" 
                sx={{ 
                  fontWeight: 600, 
                  color: 'text.secondary',
                  py: 1
                }}
              >
                {dayName}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Calendar Grid */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
          {days.map((day, index) => {
            if (day === null) {
              return (
                <Box key={`empty-${index}`} sx={{ width: 'calc(100% / 7)', p: 0.5 }}>
                  <Box sx={{ height: 120 }} />
                </Box>
              );
            }

            const dateString = formatDateForCalendar(selectedYear, selectedMonth, day);
            const dayEntries = getEntriesForDate(dateString);
            const dayTotalHours = dayEntries.reduce((sum, entry) => sum + (parseFloat(entry.hours) || 0), 0);
            const today = isToday(selectedYear, selectedMonth, day);

            return (
              <Box key={day} sx={{ width: 'calc(100% / 7)', p: 0.5 }}>
                <Card 
                  sx={{ 
                    height: 120, 
                    border: today ? 2 : 1,
                    borderColor: today ? 'primary.main' : 'divider',
                    backgroundColor: today ? 'primary.50' : 'background.paper',
                    '&:hover': {
                      backgroundColor: today ? 'primary.100' : 'grey.50'
                    }
                  }}
                >
                  <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: today ? 600 : 400,
                        color: today ? 'primary.main' : 'text.primary',
                        mb: 0.5
                      }}
                    >
                      {day}
                    </Typography>
                    
                    {dayEntries.length > 0 && (
                      <>
                        <Chip 
                          label={`${formatHours(dayTotalHours)}h`}
                          size="small"
                          color={dayTotalHours > 8 ? 'success' : 'primary'}
                          variant="outlined"
                          sx={{ fontSize: '0.7rem', height: 18, mb: 0.5 }}
                        />
                        
                        {dayEntries.slice(0, 2).map((entry, entryIndex) => (
                          <Box key={entry.id || `entry-${entryIndex}`} sx={{ mb: 0.5 }}>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                display: 'block',
                                fontSize: '0.65rem',
                                lineHeight: 1.2,
                                color: entry.isCalendarEvent ? 'primary.main' : 'text.secondary',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {entry.isCalendarEvent && '📅 '}
                              {entry.description}
                            </Typography>
                          </Box>
                        ))}
                        
                        {dayEntries.length > 2 && (
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              fontSize: '0.6rem',
                              color: 'text.secondary',
                              fontStyle: 'italic'
                            }}
                          >
                            +{dayEntries.length - 2} more
                          </Typography>
                        )}
                      </>
                    )}
                  </CardContent>

                </Card>
              </Box>
            );
          })}
        </Box>
      </Paper>
    );
  };

  // Get table data for display
  const getTableData = () => {
    // Normalize and sort all entries
    const sortedEntries = [...timeEntries].map(entry => ({
      ...entry,
      date: entry.Date || entry.date,
      description: entry.Description || entry.description,
      hours: entry.Hours || entry.hours
    })).sort((a, b) => {
      const dateA = a.date;
      const dateB = b.date;
      return dateB.localeCompare(dateA); // Sort in descending order
    });
    
    const paginatedData = sortedEntries.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    
    return {
      sortedEntries,
      paginatedData
    };
  };

  // Table rendering
  const TableView = () => {
    const { sortedEntries, paginatedData } = getTableData();
    
    return (
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Hours</TableCell>
                <TableCell align="center" width={120}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 2,
                        py: 4
                      }}
                    >
                      <CircularProgress size={60} thickness={4} />
                      <Typography variant="body1" color="text.secondary">
                        Loading time entries and calendar events...
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : timeEntries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    No time entries found for {getMonthName(selectedMonth)} {selectedYear}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((entry) => (
                  <TableRow 
                    key={entry.id || `row-${Math.random()}`}
                    hover
                    sx={{
                      backgroundColor: entry.isCalendarEvent ? 'rgba(25, 118, 210, 0.04)' : 'inherit'
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {entry.isCalendarEvent && (
                          <TimeIcon 
                            sx={{ 
                              fontSize: 16, 
                              color: 'primary.main',
                              opacity: 0.8
                            }}
                          />
                        )}
                        {formatDate(entry.date)}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2">
                          {entry.description}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      {formatHours(entry.hours)}
                    </TableCell>
                    <TableCell align="center">
                      {!entry.isCalendarEvent ? (
                        <>
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(entry)}
                            title="Edit"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(entry)}
                            title="Delete"
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </>
                      ) : (
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            fontStyle: 'italic',
                            color: 'text.secondary'
                          }}
                        >
                          Read-only
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {timeEntries.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={sortedEntries.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </Paper>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center' }}>
            <TimeIcon sx={{ mr: 2 }} />
            Time Tracker
          </Typography>
          
          {/* View Toggle */}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            aria-label="view mode"
            size="small"
          >
            <ToggleButton value="list" aria-label="list view">
              <ListViewIcon sx={{ mr: 1 }} />
              List
            </ToggleButton>
            <ToggleButton value="calendar" aria-label="calendar view">
              <CalendarViewIcon sx={{ mr: 1 }} />
              Calendar
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <IconButton onClick={loadTimeEntries} title="Refresh">
            <RefreshIcon />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddNew}
            disabled={!selectedUserId}
          >
            Add Time Entry
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
          {/* User Dropdown */}
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="user-select-label">User</InputLabel>
            <Select
              labelId="user-select-label"
              id="user-select"
              value={selectedUserId}
              label="User"
              onChange={handleUserChange}
              disabled={loadingSalesPersons}
            >
              {salesPersons.map((user) => (
                <MenuItem key={user.Email || user.email} value={user.Email || user.email}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <UserDisplay 
                      email={user.Email || user.email} 
                      size="small" 
                      showName={false}
                    />
                    <Box sx={{ ml: 2 }}>
                      {user.Name || user.name || user.displayName || user.Email || user.email}
                    </Box>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Month Navigation */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton onClick={handlePreviousMonth}>
              <ChevronLeftIcon />
            </IconButton>
            <Typography variant="h6" sx={{ minWidth: 150, textAlign: 'center' }}>
              {getMonthName(selectedMonth)} {selectedYear}
            </Typography>
            <IconButton onClick={handleNextMonth}>
              <ChevronRightIcon />
            </IconButton>
          </Box>

          {/* Total Hours */}
          <Chip
            label={`Total: ${formatHours(totalHours)} hours`}
            color="primary"
            variant="outlined"
            sx={{ ml: 'auto' }}
          />
        </Box>
        
        {/* Entry Summary and Calendar Integration Info */}
        <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Entry Type Summary */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Chip
              size="small"
              label={`Manual: ${timeEntries.filter(e => !e.isCalendarEvent).length}`}
              variant="outlined"
            />
            <Chip
              size="small"
              label={`Calendar: ${timeEntries.filter(e => e.isCalendarEvent).length}`}
              variant="outlined"
              color="primary"
            />
            {timeEntries.length > 0 && (
              <Chip
                size="small"
                label={`Total Entries: ${timeEntries.length}`}
                variant="filled"
                color="secondary"
              />
            )}
          </Box>
          
          {/* Calendar Integration Info */}
          {currentUser && currentUser.email === selectedUserId && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
              <CalendarIcon sx={{ fontSize: 16, color: 'primary.main' }} />
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Office 365 calendar events included automatically
              </Typography>
            </Box>
          )}
          
          {/* Show pagination info - only in list view */}
          {viewMode === 'list' && timeEntries.length > rowsPerPage && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Showing {Math.min((page + 1) * rowsPerPage, timeEntries.length)} of {timeEntries.length} entries
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Time Entries - List or Calendar View */}
      {viewMode === 'list' ? (
        <TableView />
      ) : (
        /* Calendar View */
        loading ? (
          <Paper sx={{ p: 6 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                py: 4
              }}
            >
              <CircularProgress 
                size={60} 
                thickness={4}
                sx={{
                  color: 'primary.main',
                  '& .MuiCircularProgress-circle': {
                    strokeLinecap: 'round',
                  }
                }}
              />
              <Typography 
                variant="body1" 
                color="text.secondary"
                sx={{ 
                  fontWeight: 500,
                  textAlign: 'center',
                  maxWidth: 300
                }}
              >
                Loading time entries and calendar events...
              </Typography>
            </Box>
          </Paper>
        ) : timeEntries.length === 0 ? (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              No time entries found for {getMonthName(selectedMonth)} {selectedYear}
            </Typography>
          </Paper>
        ) : (
          <CalendarView />
        )
      )}

      {/* Add/Edit Modal */}
      <Dialog 
        open={modalOpen} 
        onClose={handleModalClose} 
        maxWidth="sm" 
        fullWidth
        TransitionProps={{
          onEntered: () => {
            console.log('Dialog fully opened, current form data:', formData);
          }
        }}
      >
        <DialogTitle>
          {editingEntry ? 'Edit Time Entry' : 'Add New Time Entry'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              required
              fullWidth
              label="Date"
              type="date"
              name="date"
              value={formData.date || ''}
              onChange={(e) => {
                console.log('Date field change event:', e.target.value);
                handleFormChange('date', e.target.value);
              }}
              sx={{ mb: 2 }}
              InputLabelProps={{ 
                shrink: true,
                required: true
              }}
              inputProps={{
                'data-testid': 'date-input'
              }}
            />
            <TextField
              required
              fullWidth
              label="Description"
              name="description"
              multiline
              rows={3}
              value={formData.description || ''}
              onChange={(e) => handleFormChange('description', e.target.value)}
              sx={{ mb: 2 }}
              InputLabelProps={{ required: true }}
              inputProps={{
                'data-testid': 'description-input'
              }}
            />
            <TextField
              required
              fullWidth
              label="Hours"
              name="hours"
              type="number"
              inputProps={{ 
                min: 0, 
                step: 0.25,
                'data-testid': 'hours-input'
              }}
              value={formData.hours || ''}
              onChange={(e) => handleFormChange('hours', e.target.value)}
              InputLabelProps={{ required: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleModalClose}>Cancel</Button>
          <Button 
            onClick={handleSave} 
            variant="contained"
            disabled={!formData.date || !formData.description || !formData.hours}
          >
            {editingEntry ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this time entry?
          </Typography>
          {entryToDelete && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>Date:</strong> {formatDate(entryToDelete.date)}
              </Typography>
              <Typography variant="body2">
                <strong>Description:</strong> {entryToDelete.description}
              </Typography>
              <Typography variant="body2">
                <strong>Hours:</strong> {formatHours(entryToDelete.hours)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Messages */}
      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess('')}
      >
        <Alert onClose={() => setSuccess('')} severity="success">
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError('')}
      >
        <Alert onClose={() => setError('')} severity="error">
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TimeTracker; 